import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Layout, ProgressBar, TextStyle } from "@shopify/polaris";
import { useApolloClient } from "@apollo/react-hooks";
import Moment from "moment";
// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";
// import context
import { PrivateContext } from "lib/context";
// import constant
import constant from "lib/constant/constant";
// import components
import { Banner } from "lib/components";
// import query
import { GET_PRODUCT_VARIANT_DETAILS } from "app/productOld/apollo/queries";
// import hooks
import { useBatchExport } from "lib/hooks";

// import helpers
import { baseHelper } from "lib/helpers";
import productHelper from "./helper/product";

const ExportProductAsCSV = () => {
  const data = useContext(PrivateContext);
  const { cms } = data;
  const client = useApolloClient();
  const hasStartedRef = useRef(false);
  const { isSeller } = baseHelper.queryParamsFromLocation(data);
  const isVendorCSV = !JSON.parse(isSeller);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  const showErrorBanner = useCallback((title) => {
    setBanner({ title, status: constant.CRITICAL, isOpen: true });
    setTimeout(() => window.close(), 2000);
  }, []);

  const fetchPage = useCallback(
    (page, filters) =>
      client
        .query({
          query: GET_PRODUCT_VARIANT_DETAILS,
          variables: { input: { ...filters, page } },
          fetchPolicy: "network-only",
        })
        .then((res) => {
          const response = res.data.getProductVariantDetail;
          if (response.status !== constant.OK) {
            throw new Error(response.error || cms("common.message.error.noProduct"));
          }
          const { productList = [], hasMore = false, totalCount } = response.data || {};
          return { rows: productList, hasMore, totalCount };
        }),
    [client, cms]
  );

  const mapRows = useCallback((rows) => productHelper.productCSVData(rows, {}, true, false, isVendorCSV, {}, true), [
    isVendorCSV,
  ]);

  const getFileName = useCallback(() => {
    const date = Moment(new Date(), "DD-MM-YYYY").format("LL");
    return `${isVendorCSV ? "Vendor Product Export" : "Seller Product Export"} ${date}.csv`;
  }, [isVendorCSV]);

  const onError = useCallback((err) => showErrorBanner(err.message || cms("common.message.error.noProduct")), [
    showErrorBanner,
    cms,
  ]);

  const { status: exportStatus, fetchedCount, totalCount, progress, start } = useBatchExport({
    fetchPage,
    mapRows,
    getFileName,
    onError,
  });

  useEffect(() => {
    if (exportStatus === "empty") {
      showErrorBanner(cms("common.message.error.noProduct"));
    }
    if (exportStatus === "done") {
      setTimeout(() => window.close(), 500);
    }
  }, [exportStatus, showErrorBanner, cms]);

  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;

    const { id } = baseHelper.queryParamsFromLocation(data);
    const filters = { isSeller: JSON.parse(isSeller) };
    if (id) {
      filters.id = id;
    }
    start(filters);
  }, [data, isSeller, start]);

  return (
    <Layout.Section>
      {exportStatus === "loading" && (
        <div className="fullPageProgress">
          <ProgressBar progress={progress} size="medium" />
          <br />
          <TextStyle variation="subdued">
            {totalCount ? `Exporting ${fetchedCount} of ${totalCount} records...` : "Preparing export..."}
          </TextStyle>
        </div>
      )}
      {banner.isOpen && (
        <Banner
          title={banner.title}
          status={banner.status}
          isOpen={banner.isOpen}
          onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
        />
      )}
    </Layout.Section>
  );
};
export default withFeature(withErrorBoundary(ExportProductAsCSV), { feature: constant.EXPORT_PRODUCT });
